import { Injectable } from '@angular/core';
import { Observable, catchError, firstValueFrom, of } from 'rxjs';
import { EmailTemplate, EntityFieldSource, FieldSourceKind, FilterOption, TemplateMapperConfig } from '../base-template-mapper';
import { SampleBasedTemplateMapper } from '../sample-based-template-mapper';
import { TemplateContext, ReportTemplate } from '../../../models/reportTemplate.model';
import { Sample } from '../../../models/sample.model';
import { IhLabResult } from '../../../models/IhLabResult.model';
import { Lab } from '../../../models/lab.model';
import { IntegrationType } from '../../../enums/IntegrationType.enum';
import { LabRequisitionStatus } from '../../../enums/labRequisitionStatus.enum';
import { ParameterTypeEnum } from '../../../enums/parameterType.enum';
import { SampleCategoryEnum } from '../../../enums/sampleCategory.enum';
import { UnitSymbols } from '../../../enums/units.enum';
import { getNamedEnum } from '../../../_helpers/enum-helper';
import { SampleCensorSign } from '../../../enums/sampleCensorType.enum';
import { ENTITY_TYPES } from 'src/app/shared/widget-registry';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { buildCocBarcodeContent } from 'src/app/shared/utils/coc-barcode.util';
import { DateFormatService } from '../../dateFormat.service';
import { TranslateService } from '../../translate.service';
import { UserService } from '../../user.service';
import { AuthService } from '../../auth.service';
import { SampleService } from '../../sample.service';
import { AgentService } from '../../agent.service';
import { AgentGroupService } from '../../agentGroup.service';
import { OrganizationLevelService } from '../../organizationLevel.service';
import { PdfExportService } from '../../pdf-export.service';
import { ValueConversionService } from '../../value-conversion.service';
import { ChainOfCustodyLabResult, ChainOfCustodyReportEntity } from './chain-of-custody-template-mapper.model';
import { COC_LABELS, COC_LAB_FIELDS, COC_MISSING_NUMERIC_PLACEHOLDER, COC_REQUISITION_FIELDS } from './chain-of-custody-template-mapper.constants';

interface CasIdSource {
  getByIds(ids: string[]): Observable<{ id: string; casId: string }[]>;
}

interface SampleLookups {
  agentCasIds: Map<string, string>;
  agentGroupCasIds: Map<string, string>;
}

@Injectable({ providedIn: 'root' })
export class ChainOfCustodyTemplateMapper extends SampleBasedTemplateMapper<ChainOfCustodyReportEntity> {
  readonly config: TemplateMapperConfig = {
    entityType: ENTITY_TYPES.CHAIN_OF_CUSTODY,
    supportedFilters: []
  };

  constructor(
    dateFormatService: DateFormatService,
    translateService: TranslateService,
    private userService: UserService,
    private authService: AuthService,
    private sampleService: SampleService,
    private agentService: AgentService,
    private agentGroupService: AgentGroupService,
    private organizationLevelService: OrganizationLevelService,
    private pdfExportService: PdfExportService,
    private valueConversionService: ValueConversionService
  ) { super(dateFormatService, translateService); }

  protected getSamples(entity: ChainOfCustodyReportEntity): Sample[] {
    return entity.samples;
  }

  override analyzeFilters(): FilterOption[] {
    return [];
  }

  override applyFilters(): unknown[] {
    return [];
  }

  getDefaultUserInputs(): Record<string, string> {
    return {};
  }

  getEmailTemplate(): EmailTemplate {
    return { subject: 'Chain of Custody', message: '', ccEmails: [] };
  }

  getEntityFieldSources(): EntityFieldSource[] {
    const toFields = (prefix: string, names: string[]) => names.map(name => ({ path: `${prefix}.${name}`, label: name }));
    return [
      {
        key: 'requisition',
        label: 'Requisition',
        accent: 'var(--primary)',
        kind: FieldSourceKind.Inline,
        fields: toFields('requisition', [...COC_REQUISITION_FIELDS, 'turnAroundTime', 'surcharge', 'accountLabel', 'accountValue', 'reportToCityStateZip'])
      },
      {
        key: 'lab',
        label: 'Lab',
        accent: 'var(--warning)',
        kind: FieldSourceKind.Inline,
        fields: toFields('lab', [...COC_LAB_FIELDS, 'integrationTypeName', 'addressLine', 'isLims', 'isGalson', 'isAls', 'isOther'])
      },
      {
        key: 'summary',
        label: 'Summary',
        accent: 'var(--info)',
        kind: FieldSourceKind.Inline,
        fields: [
          { path: 'summary.siteNames', label: 'Site Name' },
          { path: 'summary.surveyNames', label: 'Survey' },
          { path: 'summary.sampledBy', label: 'Sampled By' },
          { path: 'summary.jurisdictions', label: 'Jurisdiction' },
          { path: 'sampleCount', label: 'Number of Samples' },
          { path: 'alternateSampleLabel', label: 'Alternate Sample Label' },
          { path: 'organization.name', label: 'Organization Name' },
          { path: 'organization.companyName', label: 'Company Name' },
          { path: 'organization.tenantLogoImg', label: 'Company Logo', isImage: true },
          { path: 'signature.relinquishedBy', label: 'Relinquished By' },
          { path: 'signature.date', label: 'Date' },
          { path: 'signature.time', label: 'Time' }
        ]
      },
      {
        key: 'labels',
        label: 'Labels',
        accent: 'var(--primary)',
        kind: FieldSourceKind.Inline,
        fields: toFields('labels', Object.keys(COC_LABELS))
      },
      {
        key: 'samples',
        label: 'Samples',
        accent: 'var(--success)',
        kind: FieldSourceKind.Inline,
        fields: [
          ...toFields('samples', [
            'sampleNumber', 'alternateSampleNumber', 'agentNames', 'agentGroupName', 'agentGroupCasId', 'samplingDate', 'volumeLiters',
            'runTimeMinutes', 'flowRateFormatted', 'startDateFormatted', 'endDateFormatted', 'isBlank', 'analyticalMethodName', 'categoryName', 'employeeName', 'taskNames', 'equipment', 'barcodeContent'
          ]),
          { path: 'samples.barcodeImg', label: 'barcodeImg', isImage: true }
        ]
      },
      {
        key: 'labResults',
        label: 'Lab Results',
        accent: 'var(--danger)',
        kind: FieldSourceKind.Inline,
        fields: toFields('samples.labResults', [
          'agentName', 'agentCasId', 'agentGroupName', 'result', 'resultValue', 'censor', 'units', 'loq', 'labSampleId', 'mass', 'twa',
          'jurisdictionName', 'oelTypeName', 'methodName'
        ])
      }
    ];
  }

  async mapToTemplateContext(
    entity: ChainOfCustodyReportEntity,
    _template: ReportTemplate,
    userInputs: Record<string, string | boolean> = {}
  ): Promise<TemplateContext> {
    const { requisition, lab, samples } = entity;
    const isLims = lab.labIntegrationType === IntegrationType.Lims;
    const isAls = lab.labIntegrationType === IntegrationType.AlsLab;
    const isCompleted = requisition.requisitionStatus === LabRequisitionStatus.Completed;
    const results = samples.flatMap(sample => sample.ihLabResults ?? []);

    const [userData, alternateSampleLabel, agentCasIds, agentGroupCasIds, siteNames] = await Promise.all([
      this.loadCachedUserProfile(this.userService),
      this.loadAlternateSampleLabel(),
      this.loadCasIds(this.agentService, results.map(r => r.agentId)),
      this.loadCasIds(this.agentGroupService, results.map(r => r.agentGroupId)),
      this.loadParentSiteNames(samples)
    ]);

    const mappedSamples = await Promise.all(samples.map(sample => this.buildSample(sample, isLims, { agentCasIds, agentGroupCasIds })));

    const currentUser = this.authService.getCurrentUser();
    const logoUrl = currentUser?.tenantLogoUrl;
    const selectedTat = requisition.tatId ? (requisition.tats?.length ? requisition.tats : lab.tats)?.find(tat => tat.id === requisition.tatId) : null;
    const labelText = (key: string) => this.translateService.getDynamicTranslation(key);

    const requisitionContext = this.createDynamicProxy(requisition, {
      ...this.nullable(requisition, COC_REQUISITION_FIELDS),
      turnAroundTime: selectedTat?.turnAroundTime || null,
      surcharge: selectedTat?.surcharge || null,
      accountLabel: labelText(isAls ? COC_LABELS.alsQuoteNumber : COC_LABELS.accountInformation),
      accountValue: (isAls ? lab.labAccountNumber : requisition.labAccountNumber || lab.labAccountNumber) || null,
      reportToCityStateZip: [requisition.reportToCity, requisition.reportToState, requisition.reportToZip].filter(Boolean).join(', ') || null
    });

    const labContext = this.createDynamicProxy(lab, {
      ...this.nullable(lab, COC_LAB_FIELDS),
      integrationTypeName: getNamedEnum(IntegrationType)[lab.labIntegrationType] ?? null,
      isLims,
      isGalson: lab.labIntegrationType === IntegrationType.GalsonLab,
      isAls,
      isOther: lab.labIntegrationType === IntegrationType.OtherLab,
      addressLine: this.buildAddressLine(lab)
    });

    return {
      requisition: requisitionContext,
      lab: labContext,
      organization: {
        name: userData?.companyName || null,
        companyName: userData?.companyName || null,
        tenantLogoImg: logoUrl?.startsWith('data:') ? logoUrl : null
      },
      summary: {
        siteNames: this.joinUnique(siteNames),
        surveyNames: this.joinUnique(samples.map(s => s.surveyName)),
        sampledBy: this.joinUnique(samples.map(s => s.sampledByUsername)),
        jurisdictions: this.joinUnique(results.map(r => r.jurisdictionName))
      },
      signature: isCompleted || entity.isPreview
        ? { relinquishedBy: null, date: null, time: null }
        : {
          relinquishedBy: [currentUser?.userFullName, currentUser?.userEmail, labelText(COC_LABELS.signedByLeap)].filter(Boolean).join('\n') || null,
          date: this.dateFormatService.formatDateTimeToString(new Date(), this.dateFormatService.userDateFormat) || null,
          time: this.dateFormatService.formatDateTimeToString(new Date(), this.dateFormatService.userTimeFormat) || null
        },
      labels: {
        ...Object.fromEntries(Object.entries(COC_LABELS).map(([key, text]) => [key, labelText(text)])),
        flowRate: `${labelText(COC_LABELS.flowRate)} (${this.valueConversionService.getParameterUnit(ParameterTypeEnum.FlowRate)})`
      },
      alternateSampleLabel,
      sampleCount: samples.length,
      samples: mappedSamples,
      userInputs: { ...this.getDefaultUserInputs(), ...userInputs },
      ...this.buildSyntheticValues(userData, this.dateFormatService)
    };
  }

  private async buildSample(sample: Sample, isLims: boolean, lookups: SampleLookups): Promise<Record<string, unknown>> {
    const results = sample.ihLabResults ?? [];
    const firstGroupCasId = results[0]?.agentGroupId ? lookups.agentGroupCasIds.get(results[0].agentGroupId) : undefined;
    const barcodeContent = isLims && firstGroupCasId
      ? buildCocBarcodeContent(firstGroupCasId, sample.startDate as string, sample.sampleNumber)
      : null;
    const dateTimeFormat = this.dateFormatService.userDateWithTimeFormat;
    const formatDate = (value: Sample['startDate']) =>
      value ? this.dateFormatService.convertUTCDateStringToUserTimeZoneString(value as string, dateTimeFormat) : null;
    const startDateFormatted = formatDate(sample.startDate);

    return this.createDynamicProxy(sample, {
      ...this.resolveSampleEnumLabels(sample),
      sampleNumber: sample.sampleNumber || null,
      alternateSampleNumber: sample.alternateSampleNumber || null,
      analyticalMethodName: sample.analyticalMethodName || null,
      agentNames: this.joinUnique(results.map(r => r.agentName || r.masterAgentName)),
      agentGroupName: sample.agentGroupName || null,
      agentGroupCasId: firstGroupCasId ?? null,
      samplingDate: startDateFormatted,
      startDateFormatted,
      endDateFormatted: formatDate(sample.endDate),
      volumeLiters: sample.sampleVolume == null
        ? COC_MISSING_NUMERIC_PLACEHOLDER
        : sample.sampleVolume.toFixed(2),
      runTimeMinutes: sample.runTime == null
        ? COC_MISSING_NUMERIC_PLACEHOLDER
        : this.valueConversionService.convertSecondsToMinutes(sample.runTime).toFixed(0),
      flowRateFormatted: sample.sampleAverageCal == null
        ? COC_MISSING_NUMERIC_PLACEHOLDER
        : sample.sampleAverageCal.toFixed(3),
      isBlank: sample.sampleCategory === SampleCategoryEnum.Blank,
      categoryName: getNamedEnum(SampleCategoryEnum)[sample.sampleCategory] ?? null,
      employeeName: sample.employee?.name || null,
      taskNames: this.joinUnique((sample.sampleTasks ?? []).map(task => task.taskName)),
      equipment: [sample.samplingEquipmentModelNumber, sample.samplingEquipmentSerialNumber].filter(Boolean).join(' / ') || null,
      barcodeContent,
      barcodeImg: barcodeContent ? await this.buildBarcodeImage(barcodeContent) : null,
      labResults: results.map(result => this.buildLabResult(result, lookups))
    });
  }

  private buildLabResult(result: IhLabResult, lookups: SampleLookups): ChainOfCustodyLabResult {
    const imported = result.concValue != null;
    const units = imported ? (UnitSymbols[result.agentUnits] ?? null) : null;
    return {
      agentName: result.agentName || result.masterAgentName || null,
      agentCasId: lookups.agentCasIds.get(result.agentId) ?? null,
      agentGroupName: result.agentGroupName || null,
      result: imported ? [result.concValue, units].filter(part => part != null).join(' ') : null,
      resultValue: imported ? result.concValue : null,
      censor: imported && result.sampleCensor != null ? SampleCensorSign[result.sampleCensor] ?? null : null,
      units,
      loq: imported ? (result.loq ?? null) : null,
      labSampleId: imported ? (result.labId || null) : null,
      mass: imported ? (result.totalMass ?? null) : null,
      twa: imported ? (result.twa ?? null) : null,
      jurisdictionName: result.jurisdictionName || null,
      oelTypeName: result.oelTypeName || null,
      methodName: result.analyticalMethodName || null
    };
  }

  private async buildBarcodeImage(content: string): Promise<string | null> {
    try {
      return await this.pdfExportService.generateBarcodeImageData(content);
    } catch {
      return null;
    }
  }

  private async loadAlternateSampleLabel(): Promise<string> {
    const fallback = this.translateService.getDynamicTranslation(COC_LABELS.alternateSampleNumber);
    const config = await firstValueFrom(this.sampleService.getFieldConfiguration().pipe(catchError(() => of(null))));
    return config?.[nameOf<Sample>(x => x.alternateSampleNumber)]?.displayName || fallback;
  }

  private async loadCasIds(source: CasIdSource, ids: (string | undefined)[]): Promise<Map<string, string>> {
    const uniqueIds = [...new Set(ids.filter((id): id is string => !!id))];
    if (!uniqueIds.length) return new Map();
    const rows = await firstValueFrom(source.getByIds(uniqueIds).pipe(catchError(() => of([]))));
    return new Map(rows.map((row): [string, string] => [row.id, row.casId]));
  }

  private async loadParentSiteNames(samples: Sample[]): Promise<(string | undefined)[]> {
    const siteIds = [...new Set(samples.map(s => s.organizationLevelId).filter(Boolean))];
    const sites = await Promise.all(siteIds.map(siteId =>
      firstValueFrom(this.organizationLevelService.getParentSite(siteId).pipe(catchError(() => of(null))))
    ));
    return sites.map(site => site?.name);
  }

  private nullable<T extends object>(source: T, keys: (keyof T)[]): Record<string, unknown> {
    return Object.fromEntries(keys.map(key => [key, source[key] || null]));
  }

  private joinUnique(values: (string | null | undefined)[]): string | null {
    return [...new Set(values.filter(Boolean))].join(', ') || null;
  }

  private buildAddressLine(lab: Lab): string | null {
    const base = [lab.labName, lab.labAddress1, lab.labAddress2, lab.labCity, lab.labState, lab.labZip].filter(Boolean).join(', ');
    return (base + (lab.labPhoneNumber ? ', ' + lab.labPhoneNumber : '') + (lab.labCellNumber ? ' | ' + lab.labCellNumber : '')) || null;
  }
}
